"use client";

import { QRCodeSVG } from "qrcode.react";
import { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiCheck, FiCopy, FiExternalLink } from "react-icons/fi";
import { useProgressNavigation } from "@/components/progress-bar";
import { Button, DateOfBirthInput, SmartDateInput, TimeInput } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectPills, MultiSelectTrigger } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { APISearchSelect, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui/select/api-search-select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { queryClient } from "@/config/react-query";
import { useClinicClock, useLocationClock } from "@/hooks/use-clinic-clock";
import { useEventTitle } from "@/hooks/use-event-title";
import { useFollowingDefault } from "@/hooks/use-following-default";
import { useLocations } from "@/hooks/use-locations";
import { useSpaKinectTreatments } from "@/hooks/use-spakinect-treatments";
import { dayMarker } from "@/lib/clinic-clock";
import { formatDate, formatTime } from "@/lib/format-data";
import { MAX_GFE_TREATMENTS, useSpakinectPortalUrl } from "@/lib/spakinect-utils";
import { toast } from "@/lib/use-toast";
import { EventStatus } from "@/types/enums/event";
import { GFEVisitType } from "@/types/enums/gfe-visit-type";
import { EventDetail, getServiceSegments } from "@/types/event";
import { GFEDetail } from "@/types/spakinect";
import { useCreateGFEAppointmentMutation, useCreateGFEInPersonMutation } from "@/api/spakinect/gfe";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
interface RequestGFEModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientEmail?: string;
  patientPhone?: string;
  patientDateOfBirth?: string;
  event?: EventDetail; // Optional event prop - when provided, date/time is auto-filled and disabled
}
export function RequestGFEModal({
  open,
  onOpenChange,
  patientId,
  patientEmail,
  patientPhone,
  patientDateOfBirth,
  event
}: RequestGFEModalProps) {
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const getEventTitle = useEventTitle();
  const {
    locations
  } = useLocations();
  // Upcoming appointments are searched on the clinic clock.
  const clock = useClinicClock();
  const getSpakinectPortalUrl = useSpakinectPortalUrl();
  const [visitType, setVisitType] = useState<GFEVisitType>(GFEVisitType.ON_DEMAND);
  const [successGfe, setSuccessGfe] = useState<GFEDetail | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [selectedTreatments, setSelectedTreatments] = useState<string[]>([]);
  // The appointment passed in, until one is picked on the patient page. Each
  // open starts from the one passed in again.
  const [selectedEvent, setSelectedEvent, {
    reset: resetSelectedEvent
  }] = useFollowingDefault(event, open);
  const [dobValue, setDobValue] = useState<string | null>(patientDateOfBirth ?? null);
  const [isDobValid, setIsDobValid] = useState(!!patientDateOfBirth);

  // Filter locations with Spakinect configuration
  const spakinectLocations = locations?.filter(loc => loc.spakinectLocationId) || [];
  const hasNoLocations = spakinectLocations.length === 0;

  // Check for missing patient contact information
  const hasMissingEmail = !patientEmail;
  const hasMissingPhone = !patientPhone;
  const hasMissingContactInfo = hasMissingEmail || hasMissingPhone;
  const currentEvent = event || selectedEvent;
  const eventLocationId = currentEvent?.location?.id;
  // The appointment's date and time, shown and checked on its location's
  // clock: a 12:30 AM New York visit is still that day to a viewer elsewhere.
  const appointmentClock = useLocationClock(eventLocationId);
  const appointmentStart = currentEvent?.start;
  const appointmentDateTime = useMemo(() => appointmentStart ? new Date(appointmentStart) : undefined, [appointmentStart]);
  const dateInputValue = appointmentDateTime && dayMarker(appointmentClock.dayOf(appointmentDateTime));

  // Check if event location matches selected location
  const hasLocationMismatch = !!currentEvent && !!selectedLocationId && !!eventLocationId && eventLocationId !== selectedLocationId;

  // Check if event location is Spakinect-enabled
  const isEventLocationSpaKinectEnabled = !!eventLocationId && spakinectLocations.some(loc_0 => loc_0.id === eventLocationId);
  const eventLocationNotSpaKinectEnabled = !!currentEvent && !!eventLocationId && !isEventLocationSpaKinectEnabled;

  // Get available treatments (filtered by favorites if set)
  const {
    treatmentOptions,
    isLoading: loadingTreatments
  } = useSpaKinectTreatments(open);

  // Create GFE mutations
  const createGFEAppointmentMutation = useCreateGFEAppointmentMutation();
  const createGFEInPersonMutation = useCreateGFEInPersonMutation();

  // Check for missing patient date of birth (only required for ON_DEMAND)
  const hasMissingDob = visitType === GFEVisitType.ON_DEMAND && !patientDateOfBirth && (!dobValue || !isDobValid);
  const isPending = [createGFEAppointmentMutation, createGFEInPersonMutation].some(mutation => mutation.isPending);
  const handleSubmit = async () => {
    if (!selectedLocationId) {
      toast({
        title: "Location Required",
        description: "Please select a location",
        variant: "error"
      });
      return;
    }
    if (selectedTreatments.length === 0) {
      toast({
        title: "Treatments Required",
        description: "Please select at least one treatment",
        variant: "error"
      });
      return;
    }

    // Validate DOB is present and complete (only for ON_DEMAND)
    if (visitType === GFEVisitType.ON_DEMAND && !patientDateOfBirth && (!dobValue || !isDobValid)) {
      toast({
        title: "Date of Birth Required",
        description: "Please enter the patient's date of birth",
        variant: "error"
      });
      return;
    }
    if (visitType === GFEVisitType.AT_HOME) {
      // When event is provided, we already have the date/time
      // When event is not provided, we need to check if an event was selected
      if (!event && !selectedEvent) {
        toast({
          title: "Appointment Required",
          description: "Please select an appointment or event",
          variant: "error"
        });
        return;
      }
      if (!appointmentDateTime) {
        toast({
          title: "Appointment Date Required",
          description: "Please select an appointment date",
          variant: "error"
        });
        return;
      }

      // Validate that appointment time is not in the past (allow today's appointments)
      if (appointmentClock.dayOf(appointmentDateTime) < appointmentClock.today()) {
        toast({
          title: "Invalid Appointment Date",
          description: "Appointment date cannot be in the past",
          variant: "error"
        });
        return;
      }
      createGFEAppointmentMutation.mutate({
        patientId,
        locationId: selectedLocationId,
        appointmentTime: appointmentDateTime.toISOString(),
        requestedTreatments: selectedTreatments,
        patientEmail,
        patientPhone,
        eventId: currentEvent?.id
      }, {
        onSuccess: () => {
          toast({
            title: "GFE Request Created",
            description: "The patient will receive an email and SMS to complete the Good Faith Exam."
          });
          onOpenChange(false);
        }
      });
    } else {
      // ON_DEMAND (on demand)
      createGFEInPersonMutation.mutate({
        patientId,
        locationId: selectedLocationId,
        requestedTreatments: selectedTreatments,
        patientEmail,
        patientPhone,
        dateOfBirth: !patientDateOfBirth ? dobValue : undefined,
        eventId: currentEvent?.id
      }, {
        onSuccess: data => {
          if (data) {
            setSuccessGfe(data);
          } else {
            toast({
              title: "GFE Request Created",
              description: "On demand Good Faith Exam has been requested."
            });
            onOpenChange(false);
          }
        }
      });
    }
  };
  const handleCopyLink = () => {
    if (successGfe?.visitLink) {
      navigator.clipboard.writeText(successGfe.visitLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Link Copied",
        description: "Visit link copied to clipboard."
      });
    }
  };
  const handleGoToLocations = () => {
    onOpenChange(false);
    navigateWithProgress("/settings/locations");
  };
  useEffect(() => {
    setIsDobValid(!!patientDateOfBirth);
    setDobValue(patientDateOfBirth ?? null);
  }, [patientDateOfBirth]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedLocationId("");
      setSelectedTreatments([]);
      setSuccessGfe(null);
      setVisitType(GFEVisitType.ON_DEMAND);
      setDobValue(patientDateOfBirth ?? null);
      setIsDobValid(!!patientDateOfBirth);
    }
  }, [open, patientDateOfBirth]);

  // Handle event selection from search select (patient page scenario)
  const handleEventSelect = (selectedEventOption: EventDetail | undefined) => {
    if (selectedEventOption?.start) setSelectedEvent(selectedEventOption);
  };

  // Format event option for search select
  const formatEventOption = (eventOption: EventDetail) => {
    const eventDate = formatDate(eventOption.start);
    const eventTime = `${formatTime(eventOption.start)} - ${formatTime(eventOption.end)}`;
    const services = getServiceSegments(eventOption.segments || []).map(es => es.service?.name).filter(Boolean).join(", ");
    const appointmentLabel = services || getEventTitle(eventOption);
    return {
      id: eventOption.id,
      name: `${eventDate} ${eventTime} - ${appointmentLabel}`,
      optionNode: <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{appointmentLabel}</span>
          </div>
          <div className="flex items-center gap-2 text-body text-muted-foreground">
            <span>{eventDate}</span>
            <span>•</span>
            <span>{eventTime}</span>
            {eventOption.location?.name && <>
                <span>•</span>
                <span>{eventOption.location.name}</span>
              </>}
          </div>
        </div>
    };
  };

  // Search function for patient events
  const searchEvents = async (searchTerm: string): Promise<EventDetail[] | null> => {
    try {
      // Only show pending, confirmed, and overridable events (exclude cancelled, no-show, etc.)
      const results = await queryClient.fetchQuery(eventQueryOptions.searchPatientEvents(patientId, searchTerm, [EventStatus.PENDING, EventStatus.CONFIRMED, EventStatus.OVERRIDABLE]));
      // Filter to only future events or events from today
      const today = clock.today();
      return results?.filter((e: EventDetail) => clock.dayOf(e.start) >= today) || [];
    } catch (error) {
      console.error("Error searching events:", error);
      return null;
    }
  };

  // Auto-select location based on event or fallback to first available
  useEffect(() => {
    // If we have an event with a location that's Spakinect-enabled, auto-select it
    if (currentEvent?.location?.id && isEventLocationSpaKinectEnabled) {
      setSelectedLocationId(currentEvent.location.id);
      return;
    }

    // Otherwise, auto-select first location if only one available and nothing is selected
    if (spakinectLocations.length === 1 && !selectedLocationId) {
      setSelectedLocationId(spakinectLocations[0].id);
    }
  }, [currentEvent?.location?.id, isEventLocationSpaKinectEnabled, spakinectLocations, selectedLocationId]);

  // Auto-select treatments from services with autoGfeTreatments when event is selected
  useEffect(() => {
    const serviceSegments = getServiceSegments(currentEvent?.segments || []);
    if (serviceSegments.length === 0 || !treatmentOptions) {
      return;
    }

    // Collect all autoGfeTreatments from event services
    const autoTreatments: string[] = [];
    for (const eventService of serviceSegments) {
      const serviceTreatments = eventService.service?.autoGfeTreatments;
      if (serviceTreatments && Array.isArray(serviceTreatments)) {
        autoTreatments.push(...serviceTreatments);
      }
    }

    // Deduplicate treatments
    const uniqueAutoTreatments = Array.from(new Set(autoTreatments));

    // Only auto-select if:
    // 1. There are auto treatments to select
    // 2. All auto treatments are available in the treatments list
    if (uniqueAutoTreatments.length > 0 && uniqueAutoTreatments.every(treatment => treatmentOptions.some(option => option.value === treatment))) {
      // Auto-select treatments when event changes
      // This will merge with any existing selections, avoiding duplicates
      setSelectedTreatments(prev => {
        const merged = Array.from(new Set([...prev, ...uniqueAutoTreatments])).sort();
        return merged.slice(0, MAX_GFE_TREATMENTS);
      });
    }
  }, [currentEvent?.id,
  // Only run when event changes
  currentEvent?.segments, treatmentOptions]);
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {successGfe ? "GFE Request Created" : "Request Good Faith Exam"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {successGfe && visitType === GFEVisitType.ON_DEMAND ? <div className="flex flex-col items-center justify-center space-y-6 py-4 text-center">
              {successGfe.visitLink ? <>
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-status-success-bg text-status-success-text">
                    <FiCheck className="h-8 w-8" />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-h2">Ready for Exam</h3>
                    <p className="text-body text-muted-foreground">
                      The on demand Good Faith Exam has been created
                      successfully. Show the QR code below to the patient or use
                      the link to start the exam.
                    </p>
                  </div>

                  <div className="flex flex-col items-center space-y-4">
                    <div className="rounded-xl border-8 border-background bg-background p-2 shadow-sm">
                      <QRCodeSVG value={successGfe.visitLink} size={200} level="H" includeMargin={false} />
                    </div>

                    <div className="flex w-full max-w-sm flex-col gap-2">
                      <Button variant="secondary" className="w-full" onClick={() => {
                  if (successGfe.visitLink) {
                    window.open(successGfe.visitLink, "_blank", "noopener,noreferrer");
                  }
                }} icon={FiExternalLink}>
                        Open Visit Link
                      </Button>
                      <Button variant="secondary" className="w-full" onClick={handleCopyLink} icon={copied ? FiCheck : FiCopy}>
                        {copied ? "Copied!" : "Copy Visit Link"}
                      </Button>
                    </div>
                  </div>
                </> : <>
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-status-warning-bg text-status-warning-text">
                    <FiAlertTriangle className="h-8 w-8" />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-h2">GFE Created Without Visit Link</h3>
                    <p className="text-body text-muted-foreground">
                      The Good Faith Exam was created but no visit link was
                      returned. You can access the GFE through the Spakinect
                      portal.
                    </p>
                  </div>

                  <Button variant="secondary" onClick={() => window.open(getSpakinectPortalUrl(successGfe.spakinectGfeId), "_blank", "noopener,noreferrer")} icon={FiExternalLink}>
                    Open Spakinect Portal
                  </Button>
                </>}

              <p className="text-caption text-muted-foreground">
                Visit ID: {successGfe.spakinectGfeId}
              </p>
            </div> : <div className="space-y-4">
              {/* Visit Type Selection */}
              <div className="space-y-2">
                <Label>Assessment Type</Label>
                <Tabs value={visitType} onValueChange={v => {
              setVisitType(v as GFEVisitType);
              // The appointment picker only shows in the AT_HOME flow; reset
              // to the prop-provided event so a stale user selection can't
              // silently leak onto an ON_DEMAND request via currentEvent.
              resetSelectedEvent();
            }} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value={GFEVisitType.ON_DEMAND}>
                      On Demand
                    </TabsTrigger>
                    <TabsTrigger value={GFEVisitType.AT_HOME}>
                      At Home
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* No Locations Warning */}
              {hasNoLocations && <div className="rounded-lg border border-status-warning-text bg-status-warning-bg p-4">
                  <div className="flex items-start gap-3">
                    <FiAlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-status-warning-text" />
                    <div className="flex-1">
                      <h4 className="font-medium text-status-warning-text">
                        No Spakinect Locations Configured
                      </h4>
                      <p className="mt-1 text-body text-status-warning-text">
                        You need to configure at least one location with a
                        Spakinect Location ID before requesting a Good Faith
                        Exam.
                      </p>
                      <Button variant="secondary" size="sm" onClick={handleGoToLocations} className="mt-3">
                        Go to Location Settings
                      </Button>
                    </div>
                  </div>
                </div>}

              {/* Missing Contact Info Warning (Only for At Home) */}
              {visitType === GFEVisitType.AT_HOME && hasMissingContactInfo && <div className="rounded-lg border border-status-warning-text bg-status-warning-bg p-4">
                  <div className="flex items-start gap-3">
                    <FiAlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-status-warning-text" />
                    <div className="flex-1">
                      <h4 className="font-medium text-status-warning-text">
                        Missing Patient Contact Information
                      </h4>
                      <p className="mt-1 text-body text-status-warning-text">
                        At home assessments require patient contact information:
                      </p>
                      <ul className="mt-2 list-inside list-disc text-body text-status-warning-text">
                        {hasMissingEmail && <li>Email address</li>}
                        {hasMissingPhone && <li>Phone number</li>}
                      </ul>
                      <p className="mt-2 text-body text-status-warning-text">
                        Please update the patient&apos;s profile or switch to On
                        Demand assessment.
                      </p>
                    </div>
                  </div>
                </div>}

              {/* Date of Birth Input (Only for ON_DEMAND when patient lacks DOB) */}
              {visitType === GFEVisitType.ON_DEMAND && !patientDateOfBirth && <div className="space-y-2">
                  <Label mandatory htmlFor="dateOfBirth">
                    Date of Birth
                  </Label>
                  <DateOfBirthInput value={dobValue ?? undefined} onChange={setDobValue} onValidityChange={setIsDobValid} maxDate={new Date()} disabled={hasNoLocations || isPending} />
                  <p className="text-caption text-muted-foreground">
                    Date of birth is required for on demand Good Faith Exams
                  </p>
                </div>}

              {/* Location Selection */}
              <div className="space-y-2">
                <Label mandatory htmlFor="location">
                  Location
                </Label>
                <Select value={selectedLocationId} onValueChange={setSelectedLocationId} disabled={hasNoLocations || visitType === GFEVisitType.AT_HOME && hasMissingContactInfo || !!currentEvent?.location?.id && isEventLocationSpaKinectEnabled}>
                  <SelectTrigger id="location">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {spakinectLocations.map(location => <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {/* Explanation when location is locked to event */}
                {!!currentEvent?.location?.id && isEventLocationSpaKinectEnabled && <p className="text-caption text-muted-foreground">
                      Location is set from the appointment (
                      {currentEvent.location?.name || <span>{"Unknown"}</span>}
                      <span>)</span>
                    </p>}
                {/* Event Location Not Spakinect-Enabled Warning */}
                {eventLocationNotSpaKinectEnabled && <div className="flex items-start gap-2 rounded-md border border-status-warning-text bg-status-warning-bg p-2">
                    <FiAlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-status-warning-text" />
                    <p className="text-caption text-status-warning-text">
                      The appointment location (
                      <span>
                        {currentEvent.location?.name || <span>{"Unknown"}</span>}
                      </span>
                      ) is not enabled for Spa Connect. Only locations with a
                      Spa Connect Location ID can be used for Good Faith Exams.
                    </p>
                  </div>}
                {/* Location Mismatch Warning */}
                {hasLocationMismatch && <div className="flex items-start gap-2 rounded-md border border-status-warning-text bg-status-warning-bg p-2">
                    <FiAlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-status-warning-text" />
                    <p className="text-caption text-status-warning-text">
                      The selected location does not match the appointment
                      location (
                      <span>
                        {currentEvent.location?.name || <span>{"Unknown"}</span>}
                      </span>
                      ). Consider selecting the matching location.
                    </p>
                  </div>}
              </div>

              {/* Appointment Date & Time Selection (Only for At Home) */}
              {visitType === GFEVisitType.AT_HOME && <>
                  {event ?
            // When event is provided (from checklist sidebar), show read-only date/time
            <div className="space-y-2">
                      <div className="flex max-w-sm items-end gap-2">
                        <div className="w-full">
                          <Label mandatory>Date</Label>
                          <SmartDateInput value={dateInputValue} side="top" disabled={true} className="max-w-[360px]" />
                        </div>
                        <div>
                          <Label mandatory>Time</Label>
                          <TimeInput date={appointmentDateTime ?? new Date()} onChange={() => {}} // No-op since disabled
                  disabled={true} timeIntervalMinutes={15} timeZone={appointmentClock.timeZone} />
                        </div>
                      </div>
                      <p className="text-caption text-muted-foreground">
                        This is the appointment date/time. The GFE must be
                        completed by this time.
                      </p>
                    </div> :
            // When no event is provided (from patient page), show event search select
            <div className="space-y-2">
                      <Label mandatory>Appointment/Event</Label>
                      <APISearchSelect value={selectedEvent} onValueChange={handleEventSelect} onSearch={searchEvents} formatOption={formatEventOption}>
                        <APISearchSelectTrigger placeholder="Search for appointment or event..." disabled={hasNoLocations || visitType === GFEVisitType.AT_HOME && hasMissingContactInfo} className="w-full" openOnFocus={true} />
                        <APISearchSelectContent emptyMessage="No appointments found. Please schedule an appointment first." />
                      </APISearchSelect>
                      {selectedEvent && <div className="space-y-2">
                          <div className="flex max-w-sm items-end gap-2">
                            <div className="w-full">
                              <Label>Date</Label>
                              <SmartDateInput value={dateInputValue} side="top" disabled={true} className="max-w-[360px]" />
                            </div>
                            <div>
                              <Label>Time</Label>
                              <TimeInput date={appointmentDateTime ?? new Date()} onChange={() => {}} // No-op since disabled
                    disabled={true} timeIntervalMinutes={15} timeZone={appointmentClock.timeZone} />
                            </div>
                          </div>
                          <p className="text-caption text-muted-foreground">
                            This is the appointment date/time. The GFE must be
                            completed by this time.
                          </p>
                        </div>}
                      {!selectedEvent && <p className="text-caption text-muted-foreground">
                          Select an appointment or event to set when the GFE
                          must be completed by
                        </p>}
                    </div>}
                </>}

              {/* Treatment Selection */}
              <div className="space-y-2">
                <Label mandatory>Treatments (Select one or more)</Label>

                {loadingTreatments ? <div className="text-body text-muted-foreground">
                    Loading treatments...
                  </div> : <MultiSelect options={treatmentOptions ?? []} value={selectedTreatments} onChange={setSelectedTreatments} placeholder="Search and select treatments..." searchPlaceholder="Search treatments..." maxSelections={MAX_GFE_TREATMENTS} disabled={hasNoLocations || visitType === GFEVisitType.AT_HOME && hasMissingContactInfo}>
                    <MultiSelectTrigger />
                    <MultiSelectContent>
                      {treatmentOptions.map(treatment_0 => <MultiSelectItem key={treatment_0.value} value={treatment_0.value}>
                          {treatment_0.label}
                        </MultiSelectItem>)}
                    </MultiSelectContent>
                    <MultiSelectPills />
                  </MultiSelect>}

                <p className="text-caption text-muted-foreground">
                  {selectedTreatments.length}/{MAX_GFE_TREATMENTS} selected •{" "}
                  <span>{treatmentOptions.length ?? 0}</span>
                  <span> available</span>
                  {selectedTreatments.length >= MAX_GFE_TREATMENTS && <span>{" (maximum reached)"}</span>}
                </p>
              </div>

              {/* Info Message */}
              <div className="rounded-md bg-status-info-bg p-3">
                <p className="text-body text-status-info-text">
                  {visitType === GFEVisitType.ON_DEMAND ? "Show the generated QR code to the patient to scan and complete their Good Faith Exam immediately." : "The patient will receive an email and SMS notification to complete the virtual Good Faith Exam."}
                </p>
              </div>
            </div>}
        </DialogBody>
        <DialogFooter>
          {successGfe ? <Button variant="secondary" onClick={() => onOpenChange(false)} className="w-full">
              Done
            </Button> : <>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} isLoading={isPending} loadingText="Creating Request..." disabled={hasNoLocations || visitType === GFEVisitType.AT_HOME && hasMissingContactInfo || hasMissingDob || isPending}>
                {visitType === GFEVisitType.ON_DEMAND ? "Start On Demand GFE" : "Request GFE"}
              </Button>
            </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}